import { lazy, type ComponentType, type SVGProps } from "react";

const Dashboard = lazy(() => import("./features/dashboard"));
const Tasks = lazy(() => import("./features/tasks"));
const Inbox = lazy(() => import("./features/inbox"));
const Kanban = lazy(() => import("./features/kanban"));
const KanbanCity = lazy(() => import("./features/kanban-city"));
const Workout = lazy(() => import("./features/workout"));
const Stats = lazy(() => import("./features/stats"));
const Flows = lazy(() => import("./features/flows"));
const More = lazy(() => import("./features/more"));
const Connect = lazy(() => import("./features/connect"));
const TamaSama = lazy(() => import("./features/tama-sama"));
const OrganicQueue = lazy(() => import("./features/organic-queue"));
const AdsQueue = lazy(() => import("./features/ads-queue"));
const Office = lazy(() => import("./features/office"));
const Workers = lazy(() => import("./features/workers"));
const Library = lazy(() => import("./features/library"));
import {
  AdsQueueIcon,
  AgentDeskIcon,
  DashboardIcon,
  FlowsIcon,
  InboxIcon,
  KanbanIcon,
  KanbanCityIcon,
  OfficeIcon,
  OrganicQueueIcon,
  StatsIcon,
  TasksIcon,
  WorkoutIcon,
  MoreIcon,
  TamaIcon,
} from "@/ui/icons";
import { LibraryIcon } from "./features/library/LibraryIcon";

/**
 * The single route registry for the app.
 *
 * Every feature owns exactly one folder under `src/features/` and appears here
 * once. Feature work must not add routing logic anywhere else — this file is
 * the only place where the surfaces are wired together.
 */

/**
 * How much horizontal room a surface wants.
 *
 * The shell used to wrap every route in `mx-auto max-w-5xl`, which is a
 * *reading measure* applied to a *canvas*: on an 1800px monitor that left 777px
 * of empty gutter while the 2120px kanban board was clipped to 45% of itself
 * (UI-REVIEW P2). The shell no longer owns width — each route declares what it
 * is, and the shell obeys.
 *
 * | intent       | width     | scroll                                  |
 * |--------------|-----------|-----------------------------------------|
 * | `measure`    | ~760px    | the page scrolls vertically             |
 * | `wide`       | ~1200px   | the page scrolls vertically             |
 * | `full-bleed` | all of it | the **feature** owns its own scrolling  |
 *
 * `measure` and `wide` are **left-aligned, not centred**: centring a 760px
 * column in an 1800px window moves the thing you are reading away from the
 * navigation you just clicked, and shifts the left edge on every resize.
 *
 * `full-bleed` is a contract, not just a width: the shell stops scrolling and
 * hands the feature a `min-h-0` box exactly the height of the viewport. A
 * full-bleed screen must manage its own overflow — that is what lets the kanban
 * board hold sticky lane headers over independently scrolling lanes.
 */
/** Pipe's account — the only one that sees personal surfaces. */
export const OWNER_EMAIL = "suebphatt@gmail.com";

export type LayoutIntent = "measure" | "wide" | "full-bleed";

export type AppRoute = {
  /** Path relative to the router root, without a leading slash. */
  path: string;
  /**
   * The one optional trailing path segment this surface is *about* — the day,
   * the project. Read and written with `usePathState(param, …)`.
   */
  param?: string;
  /** Label used by the shell navigation. */
  label: string;
  /** Optional shorter label for the compact bottom tab bar. */
  tabLabel?: string;
  /** Icon shown beside the label in the sidebar. */
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  Component: ComponentType;
  /** How much room the surface gets. See {@link LayoutIntent}. */
  layout: LayoutIntent;
  /** Route appears in the desktop sidebar unless explicitly false. */
  sidebar?: boolean;
  /** Position in the compact five-item tab bar. */
  tab?: number;
  /**
   * Shown and routed only for the account whose email is listed — a personal
   * surface that has no meaning for other users. See {@link canSeeRoute}.
   */
  onlyFor?: string;
  /**
   * Organisation data: shown and routed only for accounts on the org members
   * list (`useOrgMembership`). The server refuses everyone else regardless.
   */
  orgOnly?: boolean;
  /**
   * Query parameters this surface owns — documentation, and a guard against
   * two features quietly claiming the same name. Read them with
   * `useQueryState(name, …)`.
   */
  search?: readonly string[];
};

export const routes: AppRoute[] = [
  {
    path: "dashboard",
    label: "Dashboard",
    tabLabel: "Home",
    Icon: DashboardIcon,
    Component: Dashboard,
    layout: "wide",
    tab: 1,
  },
  {
    // Work Pipe hands to agents, and work agents hand back to be read and
    // decided on, one card per task. The old Agents page folded into it.
    // Second in the rail, right under Dashboard: it is where decisions wait.
    path: "flows",
    label: "Flows",
    Icon: FlowsIcon,
    Component: Flows,
    layout: "full-bleed",
    tab: 2,
    search: ["category", "tab", "card"],
  },
  {
    path: "tasks",
    param: "date",
    label: "Tasks",
    Icon: TasksIcon,
    Component: Tasks,
    layout: "wide",
    tab: 3,
    // "peek" opens a task's panel from a deep link — the dashboard's universal
    // search routes to `/tasks/<day>?peek=<id>`. See `lib/usePeekParam`.
    search: ["tag", "q", "peek"],
    // `#/tasks/done` is the finished-work history — the other half of the same
    // list, opened from a quiet button in the Tasks header rather than taking
    // a navigation slot of its own. See `TASKS_DONE_SEGMENT`.
  },
  {
    // Sits after Tasks because it is read in the same breath — "what did I plan"
    // then "what did somebody else ask for" — and before the board because it
    // is a daily triage surface rather than a planning one.
    path: "inbox",
    label: "Inbox",
    tabLabel: "Inbox",
    Icon: InboxIcon,
    Component: Inbox,
    layout: "wide",
    // Took the phone tab the Agents page had.
    tab: 4,
    // "status" is the New/Read/Archived/All filter and "q" the search, both of
    // which make a narrowed inbox a link. Registered here so a second surface
    // cannot quietly claim the same names. "peek" opens one item from a deep
    // link — how the dashboard's search lands here (see `lib/usePeekParam`).
    search: ["status", "q", "peek"],
  },
  {
    // Unpaid posts of every page and channel, from idea to measured, and the ones
    // worth boosting. Organisation data like Ads Queue, so members only; right
    // above Ads Queue (Pipe 2026-11-02), off the phone's tab bar (More).
    path: "organic-queue",
    label: "Organic Queue",
    Icon: OrganicQueueIcon,
    Component: OrganicQueue,
    layout: "full-bleed",
    orgOnly: true,
    // "channel" is the scope (a channel key, "bu:<unit>", or absent for all), "item" the
    // content open in the drawer, "formats"/"pillar"/"series" the queue filters and
    // "week" the Monday the calendar shows. "tab" is shared with Ads Queue's tab name.
    search: ["channel", "item", "formats", "pillar", "series", "week"],
  },
  {
    // Which ads run, wait and came down, per Business Unit. Organisation data,
    // so members only; right above Kanban, and off the phone's tab bar (More).
    path: "ads-queue",
    label: "Ads Queue",
    Icon: AdsQueueIcon,
    Component: AdsQueue,
    layout: "full-bleed",
    orgOnly: true,
    // "unit" is the selected Business Unit's key, "ad" the ad open in the viewer.
    search: ["unit", "ad"],
  },
  {
    // A pixel-art office to walk around in (docs/office.md). Everyone has a
    // personal room; org members also get the shared one, so it is not orgOnly.
    // Right under Ads Queue, and off the phone's tab bar (More).
    path: "office",
    label: "Office",
    Icon: OfficeIcon,
    Component: Office,
    layout: "full-bleed",
    // "room" is personal or org.
    search: ["room"],
  },
  {
    // The machines that pick Flows cards, one node card each (docs/workers.md).
    // Organisation data, so members only; right under Office, off the phone's
    // tab bar.
    path: "workers",
    label: "Workers",
    Icon: AgentDeskIcon,
    Component: Workers,
    layout: "wide",
    orgOnly: true,
  },
  {
    // คลัง: the shared skill packs, assets and the machines that sync them
    // (docs/library.md). Organisation data; right under Workers, off the tab bar.
    path: "library",
    label: "คลัง",
    Icon: LibraryIcon,
    Component: Library,
    layout: "wide",
    orgOnly: true,
    // "shelf" is the open tab: skills, assets or machines.
    search: ["shelf"],
  },
  {
    path: "kanban",
    param: "project",
    label: "Kanban",
    Icon: KanbanIcon,
    Component: Kanban,
    layout: "full-bleed",
    // "status" is the lane/status filter (TASK-2 phase 2) — registered here so
    // it cannot be quietly claimed by a second surface later. "peek" opens one
    // card from a deep link — how the dashboard's search lands on a board with
    // the card already open (see `lib/usePeekParam`).
    search: ["q", "status", "peek"],
  },
  {
    path: "kanban-city",
    label: "Kanban City",
    Icon: KanbanCityIcon,
    Component: KanbanCity,
    layout: "full-bleed",
  },
  {
    path: "workout",
    param: "date",
    label: "Workout",
    Icon: WorkoutIcon,
    Component: Workout,
    layout: "wide",
  },
  {
    path: "stats",
    label: "Stats",
    Icon: StatsIcon,
    Component: Stats,
    layout: "wide",
    search: ["range"],
  },
  {
    path: "tama-sama",
    label: "แก้บน Tama-sama",
    Icon: TamaIcon,
    // Pipe's own vow tracker — hidden from every other account.
    onlyFor: OWNER_EMAIL,
    Component: TamaSama,
    layout: "wide",
  },
  {
    // Reached from More and the account menu: setup, not a daily surface.
    path: "connect",
    label: "Connect Claude Code",
    Icon: AgentDeskIcon,
    Component: Connect,
    layout: "measure",
    sidebar: false,
  },
  {
    path: "more",
    label: "More",
    Icon: MoreIcon,
    Component: More,
    layout: "measure",
    sidebar: false,
    tab: 5,
  },
];

/**
 * Whether the signed-in account may see and open this surface. `orgMember` is
 * the answer of `useOrgMembership`; an organisation surface stays hidden until
 * it is `true`.
 */
export function canSeeRoute(
  route: Pick<AppRoute, "onlyFor" | "orgOnly">,
  email: string | null | undefined,
  orgMember = false,
): boolean {
  if (route.orgOnly && !orgMember) return false;
  if (!route.onlyFor) return true;
  return (email ?? "").trim().toLowerCase() === route.onlyFor;
}

export const defaultRoute = routes[0]!.path;

/**
 * Retired paths and where they live now, so a bookmark outlives the move.
 *
 * Done used to be its own section in the rail; it is now a view of Tasks. The
 * Agents page folded into Flows, and old links and widgets still open it.
 * Keys and values are router paths without the leading slash.
 */
export const redirects: Readonly<Record<string, string>> = {
  done: "tasks/done",
  "agent-desk": "flows",
};

/**
 * The pattern handed to `<Route path>`.
 *
 * The trailing parameter is optional so `#/tasks` and `#/tasks/2026-08-04` are
 * the same screen — a bare link means "today", not "no day".
 */
export function routePattern(route: AppRoute): string {
  return route.param ? `${route.path}/:${route.param}?` : route.path;
}

/** The route a pathname belongs to, or `null` for an unknown path. */
export function matchRoute(pathname: string): AppRoute | null {
  const head = pathname.replace(/^\/+/, "").split("/")[0] ?? "";
  return routes.find((route) => route.path === head) ?? null;
}

/** What the shell should do with the current pathname. */
export function layoutFor(pathname: string): LayoutIntent {
  return matchRoute(pathname)?.layout ?? "measure";
}
